import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { formatCOP } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CartDrawer({ open, onOpenChange, onCheckout, shippingFee = 0 }) {
  const { items, setQuantity, removeItem, total } = useCart();
  const grandTotal = total + (items.length > 0 ? shippingFee : 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col bg-[var(--lc-bg)] p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[var(--lc-border)] bg-white">
          <SheetTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)] flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-[var(--lc-primary)]" />
            Tu carrito
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {items.length === 0 && (
            <div className="text-center py-20 text-[var(--lc-slate)]">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-semibold">Tu carrito está vacío</p>
              <p className="text-sm">Agrega los libros del plan lector.</p>
            </div>
          )}

          {items.map((it) => (
            <div
              key={it.book_id}
              className="flex gap-3 bg-white rounded-2xl p-3 border border-[var(--lc-border)]"
              data-testid={`cart-item-${it.book_id}`}
            >
              <img
                src={it.image_url}
                alt={it.title}
                className="w-16 h-20 object-cover object-top rounded-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[var(--lc-ink)] leading-tight truncate">
                  {it.title}
                </p>
                <p className="text-xs text-[var(--lc-slate)]">{it.author}</p>
                <p className="font-display font-extrabold text-[var(--lc-ink)] mt-1">
                  {formatCOP(it.price)}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => setQuantity(it.book_id, it.quantity - 1)}
                    data-testid={`cart-decrease-${it.book_id}`}
                    className="w-7 h-7 grid place-items-center rounded-full bg-[var(--lc-bg)] border border-[var(--lc-border)] hover:bg-[var(--lc-border)]"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-6 text-center font-bold text-sm" data-testid={`cart-qty-${it.book_id}`}>
                    {it.quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(it.book_id, it.quantity + 1)}
                    data-testid={`cart-increase-${it.book_id}`}
                    className="w-7 h-7 grid place-items-center rounded-full bg-[var(--lc-bg)] border border-[var(--lc-border)] hover:bg-[var(--lc-border)]"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => removeItem(it.book_id)}
                    data-testid={`cart-remove-${it.book_id}`}
                    className="ml-auto text-[var(--lc-accent)] hover:opacity-70"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--lc-border)] bg-white px-6 py-5 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--lc-slate)] font-semibold">Subtotal</span>
                <span className="font-semibold text-[var(--lc-ink)]" data-testid="cart-subtotal">{formatCOP(total)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--lc-slate)] font-semibold">Envío</span>
                <span className="font-semibold text-[var(--lc-ink)]" data-testid="cart-shipping">{formatCOP(shippingFee)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[var(--lc-border)]">
                <span className="text-[var(--lc-slate)] font-semibold">Total</span>
                <span className="font-display font-black text-2xl text-[var(--lc-ink)]" data-testid="cart-total">
                  {formatCOP(grandTotal)}
                </span>
              </div>
            </div>
            <button
              onClick={onCheckout}
              data-testid="checkout-button"
              className="w-full rounded-full bg-[var(--lc-primary)] text-white py-4 font-display font-extrabold text-lg transition-transform duration-200 hover:-translate-y-1 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
            >
              Ir a pagar
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
